import React, { useEffect, useRef, useState } from 'react';
import { collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebaseConfig';
import './ReplyInsights.css';

const COUNTS = [10, 20, 30, 50];

const SECTIONS = [
  ['objections', 'Einwände', 'Warum jemand zögert, ablehnt oder vertagt.'],
  ['feedback', 'Feedback', 'Was an Mail, Angebot und Ton ankommt.'],
  ['unclear', 'Unklar', 'Was nachgefragt wurde und klarer werden muss.'],
  ['improvements', 'Verbessern', 'Was du an der nächsten Mail ändern solltest.'],
];

const reportsRefOf = (userId) => collection(firestore, 'world_analytics', userId, 'reply_insights');

function formatWhen(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}

function rangeLabel(sample) {
  if (!Array.isArray(sample) || sample.length === 0) return '';
  const start = formatWhen(sample[0].date);
  const end = formatWhen(sample[sample.length - 1].date);
  if (!start) return '';
  return start === end ? start : `${start} – ${end}`;
}

function ReplyInsights({ userId }) {
  const [count, setCount] = useState(10);
  const [pool, setPool] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [report, setReport] = useState(null);
  const [saved, setSaved] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [selectedId, setSelectedId] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const activeUser = useRef(userId);
  activeUser.current = userId;

  const stillViewing = (ownerId) => activeUser.current === ownerId;

  const loadSaved = async (ownerId) => {
    setSavedLoading(true);
    try {
      const snap = await getDocs(query(reportsRefOf(ownerId), orderBy('createdAt', 'desc'), limit(20)));
      const rows = [];
      snap.forEach((item) => {
        const data = item.data();
        rows.push({
          id: item.id,
          name: data.name || null,
          count: data.count || 0,
          periodLabel: data.periodLabel || '',
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.generatedAt || null,
        });
      });
      if (!stillViewing(ownerId)) return;
      setSaved(rows);
    } catch (err) {
      console.warn('[reply-insights] saved list failed:', err);
    } finally {
      if (stillViewing(ownerId)) setSavedLoading(false);
    }
  };

  const loadPreview = async (ownerId) => {
    setPreviewLoading(true);
    setError('');
    try {
      const call = httpsCallable(functions, 'analyzeReplyInsights', { timeout: 180000 });
      const result = await call({ preview: true, worldId: ownerId });
      if (!stillViewing(ownerId)) return;
      const data = result.data || {};
      const options = Array.isArray(data.options) ? data.options : [];
      setPool({
        eligible: !!data.eligible,
        available: data.available || 0,
        atLeast: !!data.atLeast,
        options,
      });
      setCount((current) => (options.includes(current) ? current : (options[0] || 10)));
    } catch (err) {
      if (!stillViewing(ownerId)) return;
      console.error('[reply-insights] preview failed:', err);
      setPool(null);
      setError(err.message || 'Die Antworten konnten nicht gezählt werden.');
    } finally {
      if (stillViewing(ownerId)) setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) return undefined;
    setReport(null);
    setSelectedId('');
    setName('');
    setCount(10);
    setError('');
    setPool(null);
    setRunning(false);
    setOpening(false);
    loadPreview(userId);
    loadSaved(userId);
    return undefined;
  }, [userId]);

  const openSaved = async (reportId) => {
    if (!userId || !reportId) return;
    const ownerId = userId;
    setOpening(true);
    setError('');
    setSelectedId(reportId);
    try {
      const snap = await getDoc(doc(firestore, 'world_analytics', userId, 'reply_insights', reportId));
      if (!snap.exists()) throw new Error('Bericht nicht gefunden.');
      const data = snap.data();
      if (!stillViewing(ownerId)) return;
      if (data.createdAt?.toDate) data.createdAt = data.createdAt.toDate().toISOString();
      setReport(data);
    } catch (err) {
      if (!stillViewing(ownerId)) return;
      setError(err.message || 'Der Bericht konnte nicht geöffnet werden.');
    } finally {
      if (stillViewing(ownerId)) setOpening(false);
    }
  };

  const removeSaved = async (reportId) => {
    if (!userId || !reportId) return;
    const ownerId = userId;
    if (!window.confirm('Diesen Bericht wirklich löschen?')) return;
    if (!stillViewing(ownerId)) return;
    setDeletingId(reportId);
    try {
      await deleteDoc(doc(firestore, 'world_analytics', ownerId, 'reply_insights', reportId));
      if (!stillViewing(ownerId)) return;
      setSaved((prev) => prev.filter((item) => item.id !== reportId));
      if (selectedId === reportId) {
        setSelectedId('');
        setReport(null);
      }
    } catch (err) {
      if (!stillViewing(ownerId)) return;
      setError(err.message || 'Der Bericht konnte nicht gelöscht werden.');
    } finally {
      if (stillViewing(ownerId)) setDeletingId(null);
    }
  };

  const analyze = async () => {
    if (!userId || running || !pool?.options?.includes(count)) return;
    const ownerId = userId;
    setRunning(true);
    setError('');
    setSelectedId('');
    try {
      const call = httpsCallable(functions, 'analyzeReplyInsights', { timeout: 180000 });
      const result = await call({
        count,
        worldId: ownerId,
        name: name.trim() || null,
      });
      if (!stillViewing(ownerId)) return;
      setReport(result.data);
      setName('');
      if (result.data?.id) {
        setSelectedId(result.data.id);
      } else {
        setError('Die Analyse ist da, das Speichern hat nicht geklappt.');
      }
      loadSaved(ownerId);
    } catch (err) {
      if (!stillViewing(ownerId)) return;
      console.error('[reply-insights] analyze failed:', err);
      setError(err.message || 'Die Analyse konnte nicht erstellt werden.');
    } finally {
      if (stillViewing(ownerId)) setRunning(false);
    }
  };

  const options = pool?.options || [];
  const availableLabel = pool
    ? `${pool.atLeast ? '50+' : pool.available} ${pool.available === 1 ? 'Antwort' : 'Antworten'}`
    : '';
  const sections = SECTIONS
    .map(([key, title, hint]) => ({
      key,
      title,
      hint,
      items: Array.isArray(report?.[key]) ? report[key] : [],
    }))
    .filter((section) => section.items.length > 0);
  const span = rangeLabel(report?.sample);

  return (
    <section className="an-card ri">
      <div className="an-bookings-head">
        <div>
          <h4 className="an-section-title">Antwort-Analyse</h4>
          <p className="an-section-sub">
            Einwände, Feedback und unklare Stellen aus den letzten Antworten.
            Ab 10 echten Antworten, höchstens die letzten 50.
          </p>
        </div>
        {pool && (
          <span className="an-badge">{availableLabel}</span>
        )}
      </div>

      <div className="an-controls">
        <div className="an-field">
          <span className="an-rr-seg-label" id="ri-count-label">Letzte Antworten</span>
          <div className="an-rr-seg ri-seg" role="group" aria-labelledby="ri-count-label">
            {COUNTS.map((n) => (
              <button
                key={n}
                type="button"
                className={`an-rr-seg-btn ${count === n && options.includes(n) ? 'is-on' : ''}`}
                disabled={previewLoading || running || opening || !options.includes(n)}
                onClick={() => setCount(n)}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div className="an-field an-field--grow">
          <label htmlFor="ri-name">Name</label>
          <input
            id="ri-name"
            type="text"
            className="an-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Optional, z.B. Oktober Einwände"
            maxLength={50}
            disabled={running || opening}
          />
        </div>

        <button
          type="button"
          className="altBtn altBtn--save"
          onClick={analyze}
          disabled={previewLoading || running || opening || !options.includes(count)}
        >
          {running ? 'Lese Antworten…' : 'Analysieren'}
        </button>
      </div>

      <div className="an-saved">
        <label htmlFor="ri-saved">Gespeicherte Analysen</label>
        <div className="an-saved-row">
          <select
            id="ri-saved"
            className="an-input"
            value={selectedId}
            disabled={running || opening || savedLoading}
            onChange={(e) => {
              const id = e.target.value;
              if (id) openSaved(id);
              else setSelectedId('');
            }}
          >
            {savedLoading ? (
              <option value="">Lade…</option>
            ) : saved.length === 0 ? (
              <option value="">Noch keine — oben analysieren</option>
            ) : (
              <>
                <option value="">Gespeicherte Analyse öffnen</option>
                {saved.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name ? `${item.name} · ` : ''}
                    {item.count} Antworten
                    {item.periodLabel ? ` · ${item.periodLabel}` : ''}
                  </option>
                ))}
              </>
            )}
          </select>
          {selectedId && (
            <button
              type="button"
              className="altBtn altBtn--petrol"
              onClick={() => removeSaved(selectedId)}
              disabled={deletingId === selectedId}
            >
              {deletingId === selectedId ? '…' : 'Löschen'}
            </button>
          )}
        </div>
      </div>

      {error && <div className="an-error">{error}</div>}

      {previewLoading && !report && (
        <div className="an-empty">Antworten im Postfach werden gezählt…</div>
      )}

      {pool?.eligible && !report && !running && !previewLoading && (
        <div className="an-empty">
          Spam, Warmup und Abwesenheit bleiben draußen. Auswählbar sind die Mengen, für die genug Antworten da sind.
        </div>
      )}

      {pool && !pool.eligible && !previewLoading && !report && (
        <div className="an-empty">
          Die Analyse startet ab 10 echten Antworten, ohne Spam, Warmup und Abwesenheit.
          Im Postfach {pool.available === 1 ? 'liegt' : 'liegen'} gerade {availableLabel}.
        </div>
      )}

      {report && (
        <div className="ri-report">
          <p className="ri-kicker">
            {report.count || report.sample?.length || count} Antworten
            {span ? ` · ${span}` : ''}
          </p>
          {report.summary ? <p className="ri-summary">{report.summary}</p> : null}
          {sections.map((section) => (
            <div key={section.key} className="ri-block">
              <h5 className="ri-block-title">{section.title}</h5>
              <p className="ri-block-hint">{section.hint}</p>
              <div className="ri-list">
                {section.items.map((item, index) => (
                  <article key={`${section.key}-${index}`} className="ri-item">
                    <div className="ri-item-top">
                      <h6 className="ri-item-title">{item.title}</h6>
                      {item.mentions > 0 && (
                        <span className="ri-mentions">
                          in {item.mentions} {item.mentions === 1 ? 'Antwort' : 'Antworten'}
                        </span>
                      )}
                    </div>
                    <p className="ri-detail">{item.detail || item.action}</p>
                    {Array.isArray(item.examples) && item.examples.map((quote, quoteIndex) => (
                      <p key={`${index}-${quoteIndex}`} className="ri-quote">{`„${quote}“`}</p>
                    ))}
                  </article>
                ))}
              </div>
            </div>
          ))}
          {!report.summary && sections.length === 0 && (
            <div className="an-empty">In diesen Antworten war kein klares Muster.</div>
          )}
        </div>
      )}
    </section>
  );
}

export default ReplyInsights;
